import { apiUrl } from '../env';

export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
  details?: unknown;
}

export class ApiException extends Error {
  readonly statusCode: number;
  readonly details: unknown;

  constructor(error: ApiError) {
    super(error.message);
    this.statusCode = error.statusCode;
    this.details = error.details;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  token?: string | null;
  _skipAuthRefresh?: boolean;
}

export interface AuthHandlers {
  refresh: () => Promise<string | null>;
  onUnauthorized: () => void;
}

let handlers: AuthHandlers | null = null;
let pendingRefresh: Promise<string | null> | null = null;

export function setAuthHandlers(next: AuthHandlers | null): void {
  handlers = next;
}

export async function runRefresh(): Promise<string | null> {
  if (!handlers) return null;
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = handlers
    .refresh()
    .catch(() => null)
    .finally(() => {
      pendingRefresh = null;
    });
  return pendingRefresh;
}

export async function api<TResponse = unknown>(path: string, opts: RequestOptions = {}): Promise<TResponse> {
  const { token, body, headers, _skipAuthRefresh, ...rest } = opts;
  const init: RequestInit = {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers ?? {}),
    },
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  };
  const response = await fetch(apiUrl(path), init);

  if (
    response.status === 401 &&
    !_skipAuthRefresh &&
    handlers &&
    !path.startsWith('/auth/refresh') &&
    !path.startsWith('/auth/login') &&
    !path.startsWith('/auth/register') &&
    !path.startsWith('/auth/2fa/challenge')
  ) {
    const newToken = await runRefresh();
    if (newToken) {
      return api<TResponse>(path, { ...opts, token: newToken, _skipAuthRefresh: true });
    }
    handlers.onUnauthorized();
  }

  if (response.status === 204) return undefined as TResponse;
  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : undefined;
  if (!response.ok) {
    throw new ApiException(
      (payload as ApiError) ?? { statusCode: response.status, message: response.statusText },
    );
  }
  return payload as TResponse;
}
